import {
  Checkbox,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AsyncSearch, { IOption } from "../../components/asyncSearch/AsyncSearch";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { PageCard } from "../../components/pageCard/PageCard";
import { CustomNoContentTableBody } from "../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import { CustomTableCell } from "../../components/table/CustomTableCell/CustomTableCell";
import CustomTablePagination from "../../components/table/CustomTablePagination/CustomTablePagination";
import CustomTableSortLabel from "../../components/table/CustomTableSortLabel/CustomTableSortLabel";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IJudgment } from "../../interfaces/IJudgment";
import { IPageProps } from "../../interfaces/IPageProps";
import { IUser } from "../../interfaces/IUser";
import api from "../../services/api";

export const JudgmentJudges: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const { exhibitionId } = useParams<{ exhibitionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toastr = useToastr();
  const { signOut } = useAuth();
  const timeout = useRef<any>(null);

  const queryUserId = searchParams.get("userId");

  const [loading, setLoading] = useState(false);
  const [judgments, setJudgments] = useState<IJudgment[]>([]);

  // Pagination state
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [orderField, setOrderField] = useState("judgmentTypeName");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");

  // AsyncSearch state
  const [judgeOptions, setJudgeOptions] = useState<IOption[]>([]);
  const [judgeOption, setJudgeOption] = useState<IOption | null>(null);
  const [judgeLoading, setJudgeLoading] = useState(false);

  // Checkbox state
  const [linkedJudgmentIds, setLinkedJudgmentIds] = useState<Set<string>>(
    new Set(),
  );
  const [checkboxLoading, setCheckboxLoading] = useState<Set<string>>(
    new Set(),
  );

  // Converter IUser para IOption
  const userToOption = (user: IUser): IOption => ({
    value: user.id || "",
    description: user.name,
  });

  // 1. Carregar julgamentos da exposição
  const loadJudgments = useCallback(
    async (
      pPage: number,
      cPage: number,
      oField: string,
      oDirection: "asc" | "desc",
    ) => {
      if (!exhibitionId) return;
      setLoading(true);
      await api
        .get(
          `judgments/all/${exhibitionId}?perPage=${pPage}&currentPage=${cPage + 1}&orderBy=${oField}&orderDirection=${oDirection.toUpperCase()}`,
        )
        .then(res => {
          setJudgments(res.data.data || []);
          setTotalRows(res.data.totalRows || 0);
        })
        .catch(error => {
          if (error?.response?.status === 401) {
            signOut();
            navigate("/");
          }
          toastr.error(
            error?.response?.data?.message || "Erro ao carregar julgamentos",
          );
        })
        .finally(() => setLoading(false));
    },
    [exhibitionId, signOut, navigate, toastr],
  );

  const handlePagination = async (
    pPage: number,
    cPage: number,
    oField: string,
    oDirection: "asc" | "desc",
  ) => {
    await loadJudgments(pPage, cPage, oField, oDirection);
  };

  useEffect(() => {
    loadJudgments(perPage, currentPage, orderField, orderDirection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadJudgments]);

  // 2. Buscar juízes dinamicamente (para o AsyncSearch)
  const handleSearchJudges = useCallback(async (filter = "") => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(async () => {
      setJudgeLoading(true);
      await api
        .get(
          `judgment-judges/dynamically?perPage=30&currentPage=1&orderBy=name&orderDirection=ASC&filter=${encodeURIComponent(filter)}`,
        )
        .then(res => {
          const users: IUser[] = res.data.data || [];
          setJudgeOptions(users.map(userToOption));
        })
        .catch(() => setJudgeOptions([]))
        .finally(() => setJudgeLoading(false));
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3. Ao selecionar um juiz, carregar vínculos com uma única requisição
  const loadLinkedJudgments = useCallback(
    async (userId: string) => {
      setLoading(true);
      await api
        .get(
          `judgment-judges/by-judge/${userId}${exhibitionId ? `?exhibitionId=${exhibitionId}` : ""}`,
        )
        .then(res => {
          const records: { judgmentId: string }[] = res.data || [];
          setLinkedJudgmentIds(new Set(records.map(r => r.judgmentId)));
        })
        .catch(error => {
          if (error?.response?.status === 401) {
            signOut();
            navigate("/");
          }
          toastr.error(
            error?.response?.data?.message ||
              "Erro ao carregar vínculos do juiz",
          );
        })
        .finally(() => setLoading(false));
    },
    [exhibitionId, signOut, navigate, toastr],
  );

  const handleSetJudgeOption = useCallback(
    async (option: IOption | null) => {
      setJudgeOption(option);
      setLinkedJudgmentIds(new Set());
      if (option?.value) {
        await loadLinkedJudgments(option.value);
      }
    },
    [loadLinkedJudgments],
  );

  useEffect(() => {
    if (queryUserId) {
      setLoading(true);
      api
        .get(`/users/${queryUserId}`)
        .then(res => {
          const user = res.data;
          setJudgeOption({
            value: user.id,
            description: user.name,
          });
          loadLinkedJudgments(user.id);
        })
        .catch(error => {
          toastr.error(
            error?.response?.data?.message || "Erro ao carregar detalhes do juiz",
          );
        })
        .finally(() => setLoading(false));
    }
  }, [queryUserId, loadLinkedJudgments, toastr]);

  // 4. Toggle do checkbox — atualização imediata com rollback em caso de erro
  const handleCheckboxChange = useCallback(
    async (judgmentId: string, checked: boolean) => {
      if (!judgeOption?.value) return;

      // Atualização otimista
      setLinkedJudgmentIds(prev => {
        const next = new Set(prev);
        if (checked) next.add(judgmentId);
        else next.delete(judgmentId);
        return next;
      });

      setCheckboxLoading(prev => new Set(prev).add(judgmentId));

      await api
        .post(`judgment-judges/${judgeOption.value}`, {
          judgments: [{ judgmentId, selected: checked }],
        })
        .catch(error => {
          // Reverter estado em caso de erro
          setLinkedJudgmentIds(prev => {
            const next = new Set(prev);
            if (checked) next.delete(judgmentId);
            else next.add(judgmentId);
            return next;
          });
          toastr.error(
            error?.response?.data?.message || "Erro ao atualizar vínculo",
          );
        })
        .finally(() => {
          setCheckboxLoading(prev => {
            const next = new Set(prev);
            next.delete(judgmentId);
            return next;
          });
        });
    },
    [judgeOption, toastr],
  );
  return (
    <>
      <TitleContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <h1>Juízes por Julgamento</h1>
          <ButtonTheme
            type="button"
            onClick={() => navigate(`/exhibition/${exhibitionId}`)}
            variant="contained"
            color="inherit"
            disabled={loading}
          >
            Voltar
          </ButtonTheme>
        </div>
      </TitleContainer>
      <PageCard>
        {/* AsyncSearch de juízes */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <AsyncSearch
              label="Selecione um juiz"
              options={judgeOptions}
              setOptions={setJudgeOptions}
              option={judgeOption}
              setOption={handleSetJudgeOption}
              asyncSearch={handleSearchJudges}
              loading={judgeLoading}
              disabled={!!queryUserId}
            />
          </Grid>
        </Grid>

        {/* Tabela de julgamentos com checkboxes */}
        {judgeOption ? (
          <>
            <TableContainer sx={{ minHeight: "250px" }}>
              <Table sx={{ padding: "16px" }}>
                <TableHead>
                  <TableRow
                    sx={{
                      "&:last-child td, &:last-child th": {
                        borderBottom: 1,
                        borderColor: "#E5E5E5",
                      },
                    }}
                  >
                    <TableCell size="small" width={60}>
                      Juiz
                    </TableCell>
                    <CustomTableSortLabel
                      perPage={perPage}
                      currentPage={currentPage}
                      orderField={orderField}
                      field="sequence"
                      label="Sequência"
                      handleRequest={handlePagination}
                      orderDirection={orderDirection}
                      setField={setOrderField}
                      setDirection={setOrderDirection}
                    />
                    <CustomTableSortLabel
                      perPage={perPage}
                      currentPage={currentPage}
                      orderField={orderField}
                      field="categoryName"
                      label="Categoria"
                      handleRequest={handlePagination}
                      orderDirection={orderDirection}
                      setField={setOrderField}
                      setDirection={setOrderDirection}
                    />
                    <TableCell size="small" width={400}>
                      Categoria Pai
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {judgments.map(judgment => (
                    <TableRow
                      key={judgment.id}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell size="small" width={60}>
                        {checkboxLoading.has(judgment.id!) ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Checkbox
                            checked={linkedJudgmentIds.has(judgment.id!)}
                            onChange={e =>
                              handleCheckboxChange(
                                judgment.id!,
                                e.target.checked,
                              )
                            }
                            size="small"
                          />
                        )}
                      </TableCell>
                      <CustomTableCell size="small" width={100}>
                        {judgment.category?.sequence}
                      </CustomTableCell>
                      <CustomTableCell size="small" width={400}>
                        {judgment.category?.name}
                      </CustomTableCell>
                      <CustomTableCell size="small" width={400}>
                        {judgment.parentCategory?.name || "—"}
                      </CustomTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {judgments.length === 0 && (
                <CustomNoContentTableBody description="Nenhum julgamento encontrado para esta exposição" />
              )}
            </TableContainer>

            <CustomTablePagination
              totalRows={totalRows}
              currentPage={currentPage}
              perPage={perPage}
              handleRequest={handlePagination}
              setPerPage={setPerPage}
              setCurrentPage={setCurrentPage}
              orderField={orderField}
              orderDirection={orderDirection}
            />
          </>
        ) : (
          <p
            style={{
              color: "#999",
              textAlign: "center",
              marginTop: "40px",
              fontSize: "14px",
            }}
          >
            Selecione um juiz acima para visualizar e gerenciar os julgamentos
            vinculados.
          </p>
        )}
      </PageCard>

      {loading && <BackdropCustom />}
    </>
  );
};
